import type React from "react";
import {
	type CSSProperties,
	type JSX,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { IMAGEKIT_BASE } from "@/lib/constants";

const DEFAULT_BREAKPOINTS = [320, 480, 640, 768, 1024, 1440, 1920] as const;

type CropOpt = "center" | "entropy" | "focus" | "faces" | "at_least" | "fit";

interface IkUrlOpts {
	w?: number;
	h?: number;
	ar?: string;
	q?: number;
	bl?: number;
	c?: CropOpt;
	v?: string;
}

function ikUrl(path: string, opts: IkUrlOpts = {}): string {
	const params = Object.entries(opts)
		.filter(([, v]) => v !== undefined && v !== null && v !== "")
		.map(([key, val]) => `${key}-${val}`)
		.join(",");

	return `${IMAGEKIT_BASE}/${path}${params ? `?tr=${params}` : ""}`;
}

function makeSrcSet(
	path: string,
	breakpoints: readonly number[],
	q: number,
	c: CropOpt,
	cacheKey?: string,
	ar?: string,
) {
	return breakpoints
		.map((w) => `${ikUrl(path, { w, q, c, v: cacheKey, ar })} ${w}w`)
		.join(", ");
}

function ikBlurUrl(path: string, crop?: CropOpt, cacheKey?: string) {
	return ikUrl(path, {
		w: 20,
		q: 20,
		bl: 30,
		c: crop ?? "center",
		v: cacheKey,
	});
}

function shimmerDataURL(width = 400, height = 300): string {
	const svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" fill="#f6f7f8"/>
      <rect id="r" width="${width}" height="${height}" fill="#eeeeee"/>
      <animate xlink:href="#r" attributeName="x" from="-${width}" to="${width}" dur="1.2s" repeatCount="indefinite"/>
    </svg>`;
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function getOptimalQuality(width: number): number {
	if (width >= 1400) return 50;
	if (width >= 1024) return 60;
	if (width >= 768) return 70;
	if (width >= 480) return 75;
	return 80;
}

const STYLE_ID = "ik-auto-component-styles";
function injectStyles() {
	if (typeof document === "undefined") return;
	if (document.getElementById(STYLE_ID)) return;

	const css = `
.ik-wrapper { position:relative; display:block; width:100%; }
.ik-img { width:100%; height:auto; opacity:0; transition:opacity .36s ease; }
.ik-img.ik-loaded { opacity:1; }
.ik-placeholder { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.ik-fill { position:absolute; inset:0; width:100%; height:100%; }
.ik-aspect-box { position:relative; width:100%; overflow:hidden; }
	`;

	const tag = document.createElement("style");
	tag.id = STYLE_ID;
	tag.innerHTML = css;
	document.head.appendChild(tag);
}

export interface ImageKitProps
	extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "width" | "height"> {
	path: string;
	darkPath?: string;
	width?: number;
	height?: number;
	fill?: boolean;
	objectFit?: CSSProperties["objectFit"];
	breakpoints?: readonly number[];
	placeholder?: "empty" | "blur" | "shimmer" | "data-url";
	sizes?: string;
	priority?: boolean;
	cacheKey?: string;
	crop?: CropOpt;
	quality?: number;
	blurDataURL?: string;
	onLoadingComplete?: (img: HTMLImageElement) => void;
	aspectRatio?: string;
}

export default function ImageKit(props: ImageKitProps): JSX.Element {
	const {
		path,
		darkPath,
		width,
		height,
		fill = false,
		objectFit = "cover",
		breakpoints = DEFAULT_BREAKPOINTS,
		placeholder = "blur",
		sizes,
		priority = false,
		cacheKey,
		crop = "center",
		blurDataURL,
		className = "",
		style,
		onLoadingComplete,
		alt,
		aspectRatio,
		quality,
		...rest
	} = props;

	// Inject styles only once
	useEffect(() => {
		injectStyles();
	}, []);

	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const imgRef = useRef<HTMLImageElement | null>(null);

	const [inView, setInView] = useState(priority);
	const [loaded, setLoaded] = useState(priority);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	const [isDark, setIsDark] = useState(false);

	useEffect(() => {
		if (!darkPath) return;
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		setIsDark(mq.matches);
		const handler = () => setIsDark(mq.matches);
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, [darkPath]);

	const effectivePath = isDark && darkPath ? darkPath : path;

	useEffect(() => {
		if (!wrapperRef.current) return;
		const el = wrapperRef.current;

		// Skip resize observer if sizes is provided explicitly
		if (sizes) {
			setContainerWidth(null); // Will signal use of 'sizes' or defaults
			return;
		}

		const update = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setContainerWidth(w);
		};

		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		return () => ro.disconnect();
	}, [sizes]);

	const sizesAttr =
		sizes ??
		(containerWidth != null ? `${Math.ceil(containerWidth)}px` : "100vw");

	const q =
		quality ?? getOptimalQuality(containerWidth ?? Math.max(...breakpoints));

	// Calculate maximum width for the src if containerWidth is not available yet
	const targetWidth = containerWidth ?? Math.max(...breakpoints);

	const finalSrc = ikUrl(effectivePath, {
		w: targetWidth,
		q,
		c: crop,
		v: cacheKey,
		ar: aspectRatio,
	});

	const srcSet = useMemo(
		() =>
			makeSrcSet(effectivePath, breakpoints, q, crop, cacheKey, aspectRatio),
		[effectivePath, breakpoints, q, crop, cacheKey, aspectRatio],
	);

	const computedBlur =
		placeholder === "blur" ? (blurDataURL ?? ikBlurUrl(effectivePath)) : null;

	const shimmer =
		placeholder === "shimmer"
			? shimmerDataURL(width ?? 400, height ?? 300)
			: null;

	useEffect(() => {
		if (priority) {
			setInView(true);
			return;
		}
		const el = imgRef.current;
		// Check for IntersectionObserver support
		if (!el || typeof IntersectionObserver === "undefined") {
			setInView(true);
			return;
		}

		const ob = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) {
						setInView(true);
						ob.disconnect();
					}
				});
			},
			{ rootMargin: "200px" }, // Load a bit earlier
		);

		ob.observe(el);
		return () => ob.disconnect();
	}, [priority]);

	useEffect(() => {
		const el = imgRef.current;
		if (!el) return;

		const onLoad = () => {
			setLoaded(true);
			onLoadingComplete?.(el);
		};

		if (el.complete && el.naturalWidth > 0) {
			onLoad();
		} else {
			el.addEventListener("load", onLoad);
			return () => el.removeEventListener("load", onLoad);
		}
	}, [onLoadingComplete]);

	const wrapperStyle: CSSProperties =
		fill && !width && !height
			? { position: "absolute", inset: 0, width: "100%", height: "100%" }
			: { width: width ? `${width}px` : "100%", maxWidth: "100%" };

	const imgStyle: CSSProperties = {
		...(fill
			? { position: "absolute", inset: 0, width: "100%", height: "100%" }
			: {}),
		objectFit,
		...style,
	};

	const renderImage = () => {
		const ph = placeholder === "blur" ? computedBlur : shimmer;

		return (
			<>
				{!inView && ph && (
					<img
						src={ph}
						alt=""
						aria-hidden
						className="ik-placeholder"
						style={{ objectFit }}
					/>
				)}

				<img
					ref={imgRef}
					src={inView ? finalSrc : (ph ?? undefined)}
					srcSet={inView ? srcSet : undefined}
					sizes={sizesAttr}
					alt={alt}
					width={width}
					height={height}
					loading={priority ? "eager" : "lazy"}
					className={`ik-img ${loaded ? "ik-loaded" : ""} ${className}`}
					style={imgStyle}
					{...rest}
				/>
			</>
		);
	};

	if (!fill && width && height) {
		return (
			<div
				ref={wrapperRef}
				className="ik-aspect-box"
				style={{ aspectRatio: `${width} / ${height}`, ...wrapperStyle }}
			>
				{renderImage()}
			</div>
		);
	}

	return (
		<div ref={wrapperRef} className="ik-wrapper" style={wrapperStyle}>
			{renderImage()}
		</div>
	);
}
